import { DOCUMENT, isPlatformServer } from "@angular/common";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import {
  Inject,
  Injectable,
  Optional,
  PLATFORM_ID,
  SecurityContext
} from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import {
  ArticleData,
  CmsData,
  DocumentData,
  AuthorData
} from "@dotcom-frontend/data";
import { Store } from "@ngrx/store";
import { REQUEST, RESPONSE } from "../../../express.tokens";
import { Request } from "express";
import { Observable, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { DynamicComponentItem } from "../../components/utils/dynamic-loader/dynamic-component-item";
import {
  setActiveLanguage,
  setAudienceNavigation,
  setBaseHref
} from "../../store/global/global.actions";
import { GlobalState } from "../../store/global/global.reducer";
import {
  selectActiveLanguage,
  selectArticleFilterCategory,
  selectSearchType
} from "../../store/global/global.selectors";
import { ComponentLoaderService } from "../component-loader/component-loader.service";

@Injectable({
  providedIn: "root"
})
export class ContentApiService {
  epiContentApiUrl: string;
  epiContentByIDApiUrl: string;
  epiSearchApiUrl: string;
  epiSearchArticlesApiUrl: string;
  epiArticleApiUrl: string;
  epiAuthorTeamApiUrl: string;
  epiSearchDocumentsApiUrl: string;
  epiAddDocumentViewCountApiUrl: string;
  epiAuthorIndexUrl: string;
  epiPreviewApiUrl: string;
  epiLenderListApiUrl: string;
  epiAccountManagerLocatorApiUrl: string;
  epiArticlesByAuthorApiUrl: string;
  epiOtherTeamMembersApiUrl: string;
  headerData: any | undefined;
  footerData: any | undefined;
  layout: DynamicComponentItem | undefined;
  isServer = isPlatformServer(this.platformId);
  category: number | null;
  filterCategory$: Observable<number | null> = this.store.select(
    selectArticleFilterCategory
  );
  activeLanguage$: Observable<{ lang: string; name: string }> =
    this.store.select(selectActiveLanguage);
  epiBlogListApiUrl: string;
  searchType$: Observable<string | undefined> =
    this.store.select(selectSearchType);
  searchType: string | undefined;
  activeLanguage = { lang: "en", name: "English" };

  constructor(
    private http: HttpClient,
    @Optional() @Inject(REQUEST) private request: Request,
    @Optional() @Inject(RESPONSE) private response: any,
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(DOCUMENT) private _document: Document,
    private store: Store<{ global: GlobalState }>,
    private domSanitizer: DomSanitizer,
    private componentService: ComponentLoaderService
  ) {
    let host;

    if (this.isServer) {
      host = this.request.headers["x-forwarded-host"];
    } else {
      host = this._document.location.hostname;
    }

    host = this.domSanitizer.sanitize(SecurityContext.URL, host as string);
    this.epiContentApiUrl = `https://${host}/api/episerver/v3.0/content?contentUrl=`;
    this.epiContentByIDApiUrl = `https://${host}/api/episerver/v3.0/content/`;
    this.epiSearchApiUrl = `https://${host}/api/landg/search/`;
    this.epiSearchArticlesApiUrl = `https://${host}/api/landg/articles/search`;
    this.epiArticleApiUrl = `https://${host}/api/landg/articles/parent/`;
    this.epiAuthorTeamApiUrl = `https://${host}/api/landg/articles/team/`;
    this.epiSearchDocumentsApiUrl = `https://${host}/api/landg/documents/search/`;
    this.epiAddDocumentViewCountApiUrl = `https://${host}/api/landg/documents/addview/`;
    this.epiPreviewApiUrl = `https://${host}/api/landg/preview/page/`;
    this.epiLenderListApiUrl = `https://${host}/api/landg/lenders/list/`;
    this.epiAccountManagerLocatorApiUrl = `https://${host}/api/landg/AccountManager/list/`;
    this.epiArticlesByAuthorApiUrl = `https://${host}/api/landg/articles/author/`;
    this.epiAuthorIndexUrl = `https://${host}/api/landg/Authors/`;
    this.epiBlogListApiUrl = `https://${host}/api/landg/articles/parent/`;
    this.epiOtherTeamMembersApiUrl = `https://${host}/api/landg/authors/team/`;
    this.store.dispatch(setBaseHref({ url: `https://${host}` }));
    this.category = null;
    this.searchType = "";
    this.activeLanguage$.subscribe((language) => {
      this.activeLanguage = language;
    });
    this.searchType$.subscribe((searchType) => {
      this.searchType = searchType;
    });
  }

  getData(url: string): Observable<any> {
    const headers = this.setHeaders();
    // TODO: LGCOM-8122 centralize sanitizing the url in one method
    const safeUrl = this.domSanitizer.sanitize(
      SecurityContext.URL,
      url as string
    );

    return this.http
      .get<CmsData>(`${this.epiContentApiUrl}${safeUrl}`, {
        observe: "response",
        headers
      })
      .pipe(
        switchMap(async (res) => {
          if (res.body && res.body[0]) {
            if (this.isServer && res.body[0].cacheControlHeader) {
              this.response.setHeader(
                "Cache-Control",
                res.body[0].cacheControlHeader
              );
            }

            if (res.body[0].language?.name) {
              this.store.dispatch(
                setActiveLanguage({
                  lang: res.body[0].language.name,
                  name: res.body[0].language.displayName
                })
              );
            }

            this.layout = await this.componentService.importComponents(
              res.body[0]
            );
            const headerData: any = res.body[0]["header"];
            if (res.body[0]["headerLinks"]) {
              this.headerData = {
                ...headerData,
                headerLinks: res.body[0]["headerLinks"]
              };
            } else {
              this.headerData = headerData;
            }
            if (res.body[0]["existingLanguages"]) {
              this.headerData = {
                ...this.headerData,
                availableLanguagesForPage: res.body[0]["existingLanguages"]
              };
            }
            this.footerData = res.body[0]["footer"];

            if (res.body[0]["footerCode"]) {
              this.footerData.footerCode = res.body[0]["footerCode"];
            }

            if (res.body[0]["preFooterContent"]) {
              this.footerData.preFooterContent =
                res.body[0]["preFooterContent"];
            }

            if (
              res.body[0]["footer"]?.footerDisclaimerText &&
              !res.body[0]["footer"]?.showDisclaimerAtTop
            ) {
              this.footerData.disclaimerComponent =
                await this.componentService.loadComponent(
                  "DisclaimerLayout",
                  "layouts/partials",
                  {
                    footerDisclaimerText:
                      res.body[0]["footer"].footerDisclaimerText
                  }
                );
            }

            if (headerData) {
              this.store.dispatch(
                setAudienceNavigation({ nav: headerData.audienceNavigation })
              );
            }
          } else {
            if (this.isServer) {
              this.response.statusCode = 404;
            }

            return 404;
          }

          return {
            header: this.headerData,
            footer: this.footerData,
            layout: this.layout
          };
        }),
        catchError(this.handleError<any>("getData", []))
      );
  }

  getDataById(id: number, options: any | undefined) {
    let url = id.toString();
    if (options) {
      url = `${url}?select=${options.toString()}`;
    }
    const headers = this.setHeaders();
    const safeUrl = this.domSanitizer.sanitize(
      SecurityContext.URL,
      url as string
    );

    return this.http
      .get<CmsData>(`${this.epiContentByIDApiUrl}${safeUrl}`, {
        observe: "response",
        headers
      })
      .pipe(
        switchMap(async (res) => {
          if (res.body) {
            return res.body;
          } else {
            if (this.isServer) {
              this.response.statusCode = 404;
            }
            return 404;
          }
        }),
        catchError(this.handleError<any>("getDataById", []))
      );
  }

  getArticleList(
    pageStyle: string,
    articleIndexPageId: number,
    page: number
  ): Observable<any> {
    const headers = this.setHeaders();
    const urlParameters: string[] = [];
    let url;

    if (pageStyle === "authorlisting") {
      url = `${this.epiAuthorTeamApiUrl}${articleIndexPageId}`;
    } else {
      url = `${this.epiArticleApiUrl}${articleIndexPageId}`;
    }

    if (page) {
      urlParameters.push(`page=${page}`);
    }

    this.filterCategory$.subscribe((category) => {
      this.category = category;
      if (this.category && this.category > 0) {
        urlParameters.push(`filterCategory=${this.category}`);
      }
    });

    url = `${url}?${urlParameters.join("&")}`;

    const safeUrl = this.domSanitizer.sanitize(
      SecurityContext.URL,
      url
    ) as string;

    return this.http
      .get<ArticleData>(safeUrl, {
        observe: "response",
        headers
      })
      .pipe(catchError(this.handleError<any>("getArticleList", [])));
  }

  search(
    query: string,
    collection: string,
    page: number,
    filters?: { [key: string]: string | string[] }
  ): Observable<any> {
    const headers = this.setHeaders();
    const urlParameters: string[] = [];
    let url =
      this.searchType === "article"
        ? `${this.epiSearchArticlesApiUrl}/${query}`
        : `${this.epiSearchApiUrl}content/`;

    if (collection) {
      urlParameters.push(`collection=${collection}`);
    }

    if (filters && Object.keys(filters).length > 0) {
      Object.entries(filters).forEach(([key, value]) => {
        const filterKey = this.getQueryParam(key);
        if (filterKey && value) {
          if (Array.isArray(value) && value.length > 0) {
            value.forEach((val) => {
              urlParameters.push(`${filterKey}=${encodeURIComponent(val)}`);
            });
          } else if (!Array.isArray(value)) {
            urlParameters.push(`${filterKey}=${encodeURIComponent(value)}`);
          }
        }
      });
    }

    if (this.searchType !== "article" && query) {
      urlParameters.push(`q=${query}`);
    }

    if (page) {
      urlParameters.push(`page=${page}`);
    }

    url = `${url}?${urlParameters.join("&")}`;
    const safeUrl = this.domSanitizer.sanitize(
      SecurityContext.URL,
      url
    ) as string;

    return this.http
      .get<ArticleData>(safeUrl, { observe: "response", headers })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError(this.handleError<any>("search", []))
      );
  }

  getQueryParam(facet: string): string | null {
    const filterMappings: { [key: string]: string } = {
      filterArticleYear: "filterArticleYear",
      filterCategory: "filterCategory",
      filterCategoryFolder: "filterCategoryFolder",
      filterCommunity: "filterCommunity",
      filterAuthor: "filterAuthor"
    };
    return filterMappings[facet] || null;
  }

  getDocumentList(
    documentLibraryPageId: number,
    page: number,
    sortOrder?: string,
    searchTerm?: string,
    categories?: Array<string>
  ): Observable<any> {
    const headers = this.setHeaders();
    let url = `${this.epiSearchDocumentsApiUrl}${documentLibraryPageId}`;
    const urlParameters = [];

    if (page) {
      urlParameters.push(`page=${page}`);
    }

    if (sortOrder) {
      urlParameters.push(`sortOrder=${sortOrder}`);
    }

    if (searchTerm) {
      urlParameters.push(`searchTerm=${searchTerm}`);
    }

    if (categories) {
      const categoriesString = categories.join(",");
      urlParameters.push(`categories=${categoriesString}`);
    }

    url = `${url}?${urlParameters.join("&")}`;
    const safeUrl = this.domSanitizer.sanitize(
      SecurityContext.URL,
      url
    ) as string;

    return this.http
      .get<DocumentData>(safeUrl, {
        observe: "response",
        headers
      })
      .pipe(catchError(this.handleError<any>("getDocumentList", [])));
  }

  addToDocumentViewCount(contentId: number): Observable<any> {
    const headers = this.setHeaders();
    let url = this.epiAddDocumentViewCountApiUrl;

    url = `${url}${contentId}`;
    const safeUrl = this.domSanitizer.sanitize(
      SecurityContext.URL,
      url
    ) as string;

    return this.http
      .post<any>(
        safeUrl,
        {},
        {
          observe: "response",
          headers
        }
      )
      .pipe(catchError(this.handleError<any>("addToDocumentViewCount", [])));
  }

  getPreviewData(url: string): Observable<any> {
    const previewId = url.split("/")[2];
    const headers = this.setHeaders();
    const safeUrl = this.domSanitizer.sanitize(
      SecurityContext.URL,
      previewId as string
    );

    return this.http
      .get<CmsData>(`${this.epiPreviewApiUrl}${safeUrl}`, {
        observe: "response",
        headers
      })
      .pipe(
        switchMap(async (res) => {
          if (res.body && res.body) {
            this.layout = await this.componentService.importComponents(
              res.body
            );
            const headerData: any = res.body["header"];
            if (res.body["headerLinks"]) {
              this.headerData = {
                ...headerData,
                headerLinks: res.body["headerLinks"]
              };
            } else {
              this.headerData = headerData;
            }
            this.footerData = res.body["footer"];

            if (this.footerData.footerDisclaimerText) {
              this.footerData.disclaimerComponent =
                await this.componentService.loadComponent(
                  "DisclaimerLayout",
                  "layouts/partials",
                  {
                    footerDisclaimerText:
                    this.footerData.footerDisclaimerText
                  }
                );
            }

            if (headerData) {
              this.store.dispatch(
                setAudienceNavigation({ nav: headerData.audienceNavigation })
              );
            }
          } else {
            if (this.isServer) {
              this.response.statusCode = 404;
            }

            return 404;
          }

          return {
            header: this.headerData,
            footer: this.footerData,
            layout: this.layout
          };
        }),
        catchError(this.handleError<any>("getPreviewData", []))
      );
  }

  setHeaders() {
    let headers = new HttpHeaders();
    headers = headers.set("Accept-Language", this.activeLanguage.lang);

    const safeUrl = this.domSanitizer.sanitize(
      SecurityContext.URL,
      this._document.location.href
    );
    if (safeUrl) {
      headers = headers.append("x-internal-referrer", safeUrl);
    }

    if (this.isServer && process.env["WHITELIST_SERVER_SECRET_FE"]) {
      headers = headers.set(
        "ApprovedServerSecret",
        process.env["WHITELIST_SERVER_SECRET_FE"]
      );
    }
    return headers;
  }

  getLenderList(id: string, fees: boolean, registration: boolean) {
    const urlParameters = [];
    let headers = new HttpHeaders();
    headers = headers.set("Cache-Control", "no-cache");

    if (fees) {
      urlParameters.push(`fees=${fees}`);
    }

    if (registration) {
      urlParameters.push(`registration=${registration}`);
    }

    const safeUrl = this.domSanitizer.sanitize(
      SecurityContext.URL,
      `${id}?${urlParameters.join("&")}` as string
    );

    if (safeUrl) {
      return this.http
        .get<any>(`${this.epiLenderListApiUrl}${safeUrl}`, {
          headers
        })
        .pipe(
          map((response) => response),
          catchError(
            this.handleError<any>("LenderAPIService", { success: false })
          )
        );
    } else {
      return of(null);
    }
  }

  getAccountManagers(id: string, postcode: string) {
    let headers = new HttpHeaders();
    headers = headers.set("Cache-Control", "no-cache");

    const areaCode = postcode.substring(0, 2);
    const urlParameters = [`postcode=${areaCode}`];

    const safeUrl = this.domSanitizer.sanitize(
      SecurityContext.URL,
      `${id}?${urlParameters.join("&")}` as string
    );

    if (safeUrl) {
      return this.http
        .get<any>(`${this.epiAccountManagerLocatorApiUrl}${safeUrl}`, {
          headers
        })
        .pipe(
          map((response) => response),
          catchError(
            this.handleError<any>("AccountManager", { success: false })
          )
        );
    } else {
      return of(null);
    }
  }

  getAuthorList(authorIndexPageId: number, page: number): Observable<any> {
    const urlParameters = [];
    let headers = new HttpHeaders();
    headers = headers.set("Cache-Control", "no-cache");
    let url = `${this.epiAuthorIndexUrl}${authorIndexPageId}`;

    if (page) {
      urlParameters.push(`page=${page}`);
    }

    url = `${url}?${urlParameters.join("&")}`;
    const safeUrl = this.domSanitizer.sanitize(
      SecurityContext.URL,
      url
    ) as string;

    return this.http
      .get<AuthorData>(safeUrl, {
        observe: "response",
        headers
      })
      .pipe(catchError(this.handleError<any>("getAuthorList", [])));
  }

  getArticlesByAuthor(authorId: number, page: number) {
    let headers = new HttpHeaders();
    headers = headers.set("Cache-Control", "no-cache");
    const urlParameters = [];

    if (page) {
      urlParameters.push(`page=${page}`);
    }

    const safeUrl = this.domSanitizer.sanitize(
      SecurityContext.URL,
      `${authorId}?${urlParameters.join("&")}` as string
    );

    if (safeUrl) {
      return this.http
        .get<any>(`${this.epiArticlesByAuthorApiUrl}${safeUrl}`, {
          headers
        })
        .pipe(
          map((response) => response),
          catchError(
            this.handleError<any>("ArticlesByAuthor", { success: false })
          )
        );
    } else {
      return of(null);
    }
  }

  geOtherTeamMembers(authorId: number) {
    let headers = new HttpHeaders();
    headers = headers.set("Cache-Control", "no-cache");

    const safeUrl = this.domSanitizer.sanitize(
      SecurityContext.URL,
      `${this.epiOtherTeamMembersApiUrl}${authorId}` as string
    );

    if (safeUrl) {
      return this.http
        .get<any>(`${safeUrl}`, {
          headers
        })
        .pipe(
          map((response) => response),
          catchError(
            this.handleError<any>("OtherTeamMembers", { success: false })
          )
        );
    } else {
      return of(null);
    }
  }

  getBlogList(indexPageId: number, page: number, filterOption?: string) {
    const urlParameters = [];
    let headers = new HttpHeaders();
    headers = headers.set("Cache-Control", "no-cache");

    if (page) {
      urlParameters.push(`page=${page}`);
    }

    if (filterOption && filterOption != "0") {
      urlParameters.push(`filterCommunity=${filterOption}`);
    }

    const safeUrl = this.domSanitizer.sanitize(
      SecurityContext.URL,
      `${indexPageId}?${urlParameters.join("&")}` as string
    );

    if (safeUrl) {
      return this.http
        .get<any>(`${this.epiArticleApiUrl}${safeUrl}`, {
          headers
        })
        .pipe(
          map((response) => response),
          catchError(this.handleError<any>("getBlogList", { success: false }))
        );
    } else {
      return of(null);
    }
  }

  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<T> => {
      if (this.isServer) {
        this.response.statusCode = error.status;
      }

      console.error(error);
      console.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
